import { Fragment, useCallback, useMemo } from 'react'

import { FeatureFlagName } from '@api'
import { useUnit } from 'effector-react'

import { NavSection } from '@/components/layout/sidebar/nav/nav-section.tsx'
import type { NavSingleButtonProps } from '@/components/layout/sidebar/nav/nav-single-button.tsx'
import { $account } from '@/models/account.ts'
import {
  $creators,
  $currentCreator,
  $isCreatorProcessCompleted,
} from '@/models/creators.ts'
import { $availableFeatures } from '@/models/feature-flags'
import { hasPermissionsToRoute } from '@/modules/accounts/state/AccessControlGuard'
import { isFeatureAvailable } from '@/utils/featureFlags'
import { canSeeOverview } from '@/utils/helpers'

import { SIDEBAR_ITEMS } from './constants'

export const mapNavigationItem = (
  item: NavSingleButtonProps,
  creatorId: number,
): NavSingleButtonProps => {
  return {
    ...item,
    href: item.href.replace(':creatorId', String(creatorId)),
    children: item.children?.map((child) =>
      mapNavigationItem(child, creatorId),
    ),
  }
}

interface Props {
  onCollapse: (collapsed: boolean) => void
}

export const useSidebarSections = ({ onCollapse }: Props) => {
  const account = useUnit($account)
  const creators = useUnit($creators)
  const activeCreator = useUnit($currentCreator)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const availableFeatures = useUnit($availableFeatures)

  const isItemVisible = useCallback(
    (item: NavSingleButtonProps) => {
      const hasPermission =
        !isCreatorProcessCompleted ||
        (item.title !== 'Overview' &&
          hasPermissionsToRoute(
            activeCreator,
            item.creatorPermission,
            item.creatorFeature,
            account,
            item.accountFeature,
            creators,
            item.onlyOwner,
            item.onlyAdmin,
          )) ||
        (item.title === 'Overview' &&
          canSeeOverview(account, creators, isCreatorProcessCompleted))

      return (
        isFeatureAvailable(availableFeatures, item.featureFlagName) &&
        hasPermission &&
        (!item.canSee || item.canSee(activeCreator, account))
      )
    },
    [
      activeCreator,
      account,
      creators,
      isCreatorProcessCompleted,
      availableFeatures,
    ],
  )

  const handleCollapseSidebar = useCallback(() => {
    onCollapse(true)
  }, [onCollapse])

  const sidebarItems = useMemo(
    () =>
      availableFeatures?.includes(FeatureFlagName.TASKS_PAGE)
        ? SIDEBAR_ITEMS
        : SIDEBAR_ITEMS.map((section) => ({
            ...section,
            items: section.items.filter((item) => item.title !== 'Tasks'),
          })),
    [availableFeatures],
  )

  const sections = useMemo(
    () =>
      sidebarItems.map((section, index) => {
        const filteredItems = section.items
          .filter((item) => {
            if (item.children?.length) {
              return item.children.some((child) => isItemVisible(child))
            }

            return isItemVisible(item)
          })
          .map((item) => {
            if (item.children) {
              return {
                ...item,
                children: item.children.filter((child) => isItemVisible(child)),
              }
            }

            return item
          })
        const sectionItems = filteredItems.map((item) =>
          mapNavigationItem(item, activeCreator?.id ?? 0),
        )

        if (sectionItems.length === 0) {
          return null
        }

        return (
          <Fragment key={index}>
            {index !== 0 && section.separator === 'marginTop' && (
              <div
                className="mt-auto"
                key={`sidebar-divider-${index}`}
              >
                &nbsp;
              </div>
            )}

            {index != 0 && section.separator === 'border' && (
              <hr className="my-2" />
            )}

            <NavSection
              account={account}
              key={index}
              items={sectionItems}
              onClick={handleCollapseSidebar}
            />
          </Fragment>
        )
      }),
    [
      activeCreator,
      account,
      handleCollapseSidebar,
      isItemVisible,
      sidebarItems,
    ],
  )

  return sections
}
